<template>
    <div class="loader" :class="{ center: position !== 'left' }">
        <div
            class="inner"
            :class="{
                'text-6xl': size === '2xl',
                'text-5xl': size === 'xl',
                'text-4xl': !size || size === 'lg',
                'text-2xl': size === 'md',
                'text-xl': size === 'sm'
            }"
        >
            <div class="spinner">
                <i class="fad fa-spinner-third fa-spin loader__spinner" />
                <span v-if="emojiIndex > 0 && !isEmbeddedApp" class="emoji-pulse">{{ emojis[emojiIndex - 1] }}</span>
            </div>
            <div v-if="emojiIndex > 0" class="still-loading-container">
                <div class="still-loading">Still loading...</div>
                <div v-if="emojiIndex === emojis.length" class="support-message">
                    Check your Internet connection,<br />
                    {{ isWorkstation ? 'restart ZynoWorkstation' : 'refresh the page' }}, and try again.<br />
                    If the problem persists, please<br />
                    reach out to ZynoSuite support.
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

import { isEmbeddedApp, isWorkstationRef as isWorkstation } from '@/global.shared';

const props = defineProps<{
    position?: 'left' | 'center';
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    indefinite?: boolean;
}>();

const emojis = ['⏳', '🙃', '🤨', '😕', '🥺', '😢', '😭', '🫠', '😵‍💫', '😵', '🤡', '❌'];
const emojiIndex = ref(0);
let timer: ReturnType<typeof setTimeout> | null = null;

function scheduleNext() {
    if (props.indefinite) return;
    if (emojiIndex.value >= emojis.length) return;
    const delay = emojiIndex.value === 0 ? 5000 : 2000;
    timer = setTimeout(() => {
        emojiIndex.value++;
        scheduleNext();
    }, delay);
}

onMounted(() => {
    scheduleNext();
});

onUnmounted(() => {
    if (timer) clearTimeout(timer);
});
</script>

<style scoped>
@reference '#tw.css';

i,
.emoji-pulse {
    font-size: inherit;
}

.loader__spinner {
    color: var(--accent);
}

.still-loading-container {
    @apply flex flex-col items-center;
}

.spinner {
    @apply relative;
}

.emoji-pulse {
    position: absolute;
    display: block;
    animation: pulse 1s infinite ease-in-out;
    transform: scale(0.4);
    top: 4%;
}

.still-loading {
    @apply text-sm text-ink-3 mt-2;
    animation: pulse 1s infinite ease-in-out;
}

.support-message {
    @apply text-xs text-ink-3 mt-6 text-center;
}

@keyframes pulse {
    0%,
    100% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.15);
        opacity: 0.7;
    }
}

.loader {
    @apply p-4 flex;

    &.center {
        @apply justify-center;
    }
}

.inner {
    @apply flex flex-col items-center relative;

    &.text-6xl {
        @apply text-[3.75rem];
    }

    &.text-5xl {
        @apply text-[3rem];
    }

    &.text-4xl {
        @apply text-[2.25rem];
    }

    &.text-2xl {
        @apply text-[1.5rem];
    }

    &.text-xl {
        @apply text-[1.25rem];
    }
}
</style>
